import { useCallback, useEffect, useMemo, useState } from 'react';

import { noop, useBoolean } from '@/_shared';

import type { RAsyncComboBoxProps, RAsyncItemsRendererProps, RAsyncShowMoreButtonRendererProps } from './types';

import { Select } from '../react-select';

export type RSyncMultiComboBoxProps<T> = Omit<
  RAsyncComboBoxProps<T, false>,
  | 'isMulti'
  | 'selectedItems'
  | 'disabled'
  | 'onValueChange'
  | 'filterItems'
  | 'filterOption'
  | 'useApi'
  | 'apiParams'
  | 'mutationOptions'
> & {
  options: T[];
  selectedItems: T[];
  paginatedItems?: number;
  isEditable?: boolean;
  filterOptions?: (item: T) => boolean;
  onValueChange: (values: T[]) => void;
  sortValues?: (values: T[]) => T[];
  renderItems: RAsyncItemsRendererProps<T>;
  renderShowMoreButton?: RAsyncShowMoreButtonRendererProps;
};

export function RSyncMultiCombobox<T>({
  options = [],
  selectedItems = [],
  paginatedItems,
  isEditable = false,
  onValueChange = noop,
  filterOptions = () => false,
  renderItems,
  renderShowMoreButton,
  sortValues,
  keyExtractor,
  itemToString,
  ...props
}: RSyncMultiComboBoxProps<T>) {
  const [values, setValues] = useState<T[]>(selectedItems);
  const [isShowingAll, showAll] = useBoolean(!paginatedItems);

  const showAllOptions = isShowingAll || isEditable;

  const valueChangeHandler: RAsyncComboBoxProps<T, false>['onValueChange'] = (newValues) => {
    if (sortValues) {
      onValueChange(sortValues([newValues as T, ...values]));
    } else {
      onValueChange([newValues as T, ...values]);
    }
  };

  const removeItemHandler = useCallback(
    (item: T) => {
      setValues((prev) => {
        const newValues = prev.filter((prevItem) => keyExtractor(prevItem) !== keyExtractor(item));
        onValueChange(newValues);
        return newValues;
      });
    },
    [keyExtractor, onValueChange]
  );

  useEffect(() => {
    setValues(selectedItems);
  }, [selectedItems]);

  const renderItemProps = useMemo(
    () => ({
      values: showAllOptions ? values : values.slice(0, paginatedItems),
      removeItem: removeItemHandler,
    }),
    [showAllOptions, values, paginatedItems, removeItemHandler]
  );

  const showMoreButtonProps = useMemo(
    () => ({
      onClick: showAll.toggle,
      text: isShowingAll ? 'Show less' : 'Show more',
    }),
    [isShowingAll, showAll.toggle]
  );

  const filteredOptions = useMemo(
    () => options.filter((option) => !filterOptions(option as T)),
    [options, filterOptions]
  );

  return (
    <div className="flex flex-col gap-8">
      {isEditable && (
        <Select<T, false>
          {...props}
          isMulti={false}
          onChange={valueChangeHandler}
          getOptionLabel={itemToString}
          getOptionValue={keyExtractor}
          options={filteredOptions}
        />
      )}
      <div className="text-c-card-foreground!">
        {renderItems(renderItemProps)}
        {!isEditable && paginatedItems && renderShowMoreButton && values.length > paginatedItems
          ? renderShowMoreButton?.(showMoreButtonProps)
          : null}
      </div>
    </div>
  );
}
